import { z } from "zod";

import { CONTRACT_BLOCK_KINDS } from "./contractContent";

/**
 * AI SÖZLEŞME TASLAĞI (KARAR 07 dilim 2 — CLOS-239 [187]).
 *
 * ### ⚠ Girdi alanları MOCKUP FORMUNDAN BİREBİR
 *
 * "AI ile Taslak Oluştur" sihirbazının 2. adımı (Girdi Bilgileri / Taraflar ve Tercihler)
 * dokuz alan sayıyor: amaç, tür, dil, müvekkil, karşı taraf, ek notlar, detay seviyesi,
 * üslup, standart maddeler. Kartsız bir alan İCAT EDİLMEDİ — dokuzu da burada, ne eksik
 * ne fazla.
 *
 * ### ⚠ Yalnız `purpose` ve `contractType` ZORUNLU
 *
 * Kart: *"Zorunlu girdiler ... işlem öncesi doğrulanır."* Sözleşmenin amacı ve türü
 * olmadan bir taslak üretmenin anlamı yok; geri kalan dokuz alanın yedisi BAĞLAM —
 * eksikse model `missingFields`'e yazar (CLOS-239 kriter: *"eksik alanlar ... gösterilir"*),
 * uydurmaz.
 */
export const CONTRACT_AI_DRAFT_DETAIL_LEVELS = ["kisa", "standart", "detayli"] as const;
export type ContractAiDraftDetailLevel = (typeof CONTRACT_AI_DRAFT_DETAIL_LEVELS)[number];

export const CONTRACT_AI_DRAFT_DETAIL_LEVEL_NAMES: Record<ContractAiDraftDetailLevel, string> = {
  kisa: "Kısa",
  standart: "Standart",
  detayli: "Detaylı",
};

export const contractAiDraftInputSchema = z.strictObject({
  purpose: z.string().min(1, "Sözleşmenin amacı gereklidir").max(2000),
  contractType: z.string().min(1, "Sözleşme türü gereklidir").max(200),
  /** ⚠ Ürün bugün yalnız Türkçe üretiyor (`gemini.ts` sistem yönergesi); alan yine de
   *  sürümlü VE görünür — sihirbaz ekranı bunu göstermeli, sessizce Türkçe varsaymamalı. */
  language: z.string().min(2).max(10).default("tr"),
  clientName: z.string().max(300).default(""),
  counterpartyName: z.string().max(300).default(""),
  additionalNotes: z.string().max(4000).default(""),
  detailLevel: z.enum(CONTRACT_AI_DRAFT_DETAIL_LEVELS).default("standart"),
  tone: z.string().max(200).default(""),
  standardClauses: z.array(z.string().max(200)).max(30).default([]),
});
export type ContractAiDraftInput = z.infer<typeof contractAiDraftInputSchema>;

/**
 * Model çıktısının BELGE kısmı — `contractDocumentSchema`'nın (`contractContent.ts`)
 * KISITLI bir yansıması.
 *
 * ⚠ `clause.id` YOK: madde kimliği istemcinin/editörün ürettiği bir değer (`z.uuid()`).
 * Modelin bir UUID uydurmasını istemek onu gereksiz bir sorumlulukla yükler ve
 * çakışma/biçim riski taşır — sunucu, kabul anında her maddeye KENDİ ürettiği kimliği
 * atar (`aiDraft.ts`: `belgeyeCevir`).
 */
export const contractAiDraftBlockSchema = z.strictObject({
  kind: z.enum(CONTRACT_BLOCK_KINDS),
  text: z.string().min(1).max(20_000),
});
export const contractAiDraftClauseSchema = z.strictObject({
  title: z.string().max(300),
  blocks: z.array(contractAiDraftBlockSchema).max(500),
});
export const contractAiDraftDocumentSchema = z.strictObject({
  clauses: z
    .array(contractAiDraftClauseSchema)
    .min(1, "Taslak en az bir madde içermelidir")
    .max(1000),
});
export type ContractAiDraftDocument = z.infer<typeof contractAiDraftDocumentSchema>;

/**
 * Model çıktısının TAMAMI (KARAR 11 · CLOS-239 kriter: *"kaynaklar, varsayımlar, eksik
 * alanlar ve insan incelemesi gereği gösterilir"*).
 *
 * ⚠ "Kaynaklar" burada CLOS-205 analizindeki gibi bir sözleşme METNİNE referans DEĞİL —
 * üretilen bir taslağın kaynaklanacağı önceden var olan bir belge yok. Bu kartın okuması:
 * hangi GİRDİ ALANLARININ kullanıldığı/eksik bırakıldığı `missingFields`'te görünür oluyor;
 * bir varsayım gerektiren her karar `assumptions`'a yazılıyor. İkisi birlikte kartın
 * *"insan incelemesi gereği"* kriterini karşılıyor.
 */
export const contractAiDraftOutputSchema = z.strictObject({
  document: contractAiDraftDocumentSchema,
  /** Modelin girdi eksikliğinde YAPTIĞI varsayımlar — serbest metin, UYDURMA yasağı yönergede. */
  assumptions: z.array(z.string().max(500)).max(50).default([]),
  /** Kullanıcının doldurmadığı ama taslağı etkileyebilecek alanlar. */
  missingFields: z.array(z.string().max(300)).max(50).default([]),
});
export type ContractAiDraftOutput = z.infer<typeof contractAiDraftOutputSchema>;

export const CONTRACT_AI_DRAFT_STATUSES = ["completed", "failed"] as const;
export type ContractAiDraftStatus = (typeof CONTRACT_AI_DRAFT_STATUSES)[number];

export const CONTRACT_AI_DRAFT_STATUS_NAMES: Record<ContractAiDraftStatus, string> = {
  completed: "Tamamlandı",
  failed: "Başarısız",
};

/** `contract_ai_analyses` ile AYNI gerekçe: serbest metin yok, sağlayıcı gövdesi sızmasın. */
export const CONTRACT_AI_DRAFT_ERROR_CODES = ["provider_error", "invalid_output"] as const;
export type ContractAiDraftErrorCode = (typeof CONTRACT_AI_DRAFT_ERROR_CODES)[number];

export const CONTRACT_AI_DRAFT_ERROR_MESSAGES: Record<ContractAiDraftErrorCode, string> = {
  provider_error: "AI sağlayıcısına ulaşılamadı; taslak üretilemedi ve krediniz iade edildi.",
  invalid_output:
    "AI beklenen biçimde bir taslak üretmedi; sonuç kaydedilmedi ve krediniz iade edildi.",
};

export const AI_DRAFT_NOT_ACCEPTABLE_MESSAGE =
  "Yalnız tamamlanmış bir AI taslağı kabul edilebilir.";
export const AI_DRAFT_ALREADY_ACCEPTED_MESSAGE = "Bu taslak zaten bir sözleşmeye dönüştürüldü.";
export const AI_DRAFT_HUMAN_APPROVAL_REQUIRED_MESSAGE =
  "Taslağı sözleşmeye dönüştürmek için önce açıkça kabul etmelisiniz.";

/** Kabul talebi — CLOS-990 insan-onayı köprüsünün gövdedeki karşılığı. */
export const acceptContractAiDraftSchema = z.strictObject({
  /** ⚠ ZORUNLU VE LİTERAL `true`: örtük/varsayılan bir onay yok (CLOS-208 kriter 9-10). */
  confirmed: z.literal(true, { error: "Taslağı kabul etmek için onay gereklidir" }),
});
export type AcceptContractAiDraftInput = z.infer<typeof acceptContractAiDraftSchema>;

export type ContractAiDraftSummary = {
  id: string;
  status: ContractAiDraftStatus;
  input: ContractAiDraftInput;
  output: ContractAiDraftOutput | null;
  errorCode: ContractAiDraftErrorCode | null;
  model: string;
  promptVersion: number;
  schemaVersion: number;
  credits: number;
  normalizedPages: number;
  coefficientVersion: number;
  /**
   * ⚠ Ayrı bir `saved` alanı YOK: `contract_ai_analyses`'teki `saved` booleanının bu
   * karttaki karşılığı `acceptedContractId`'nin `null` olup olmaması — nullable bir
   * kimlik hem "kaydedildi mi" sorusunu hem GERÇEK hedefi tek alanda taşıyor.
   */
  acceptedContractId: string | null;
  acceptedAt: string | null;
  acceptedByName: string | null;
  createdByName: string;
  createdAt: string;
};
